import { z } from "zod";

import { stripCompatibilityFeatures, unescapeHTML } from "@/utils/html";
import { getStatusMentions } from "@/utils/status";

import { accountSchema } from "./account";
import { attachmentSchema } from "./attachment";
import { cardSchema } from "./card";
import { customEmojiSchema } from "./custom-emoji";
import { embeddedAnnouncementSchema } from "./embedded-announcement";
import { emojiReactionSchema } from "./emoji-reaction";
import { groupSchema } from "./group";
import { mentionSchema } from "./mention";
import { metricsSchema } from "./metrics";
import { pollSchema } from "./poll";
import { predictionSchema } from "./prediction";
import { sportGameDetailsSchema } from "./sport-game";
import { sportNewsArticleSchema } from "./sport-news";
import { contentSchema, dateSchema, filteredArray } from "./utils";

import type { Resolve } from "@/utils/types";

const statusPleromaSchema = z.object({
  emoji_reactions: filteredArray(emojiReactionSchema),
  quote: z.literal(null).catch(null),
  quote_visible: z.boolean().catch(true),
});

const statusTagSchema = z.object({
  name: z.string(),
});

const baseStatusSchema = z.object({
  account: accountSchema,
  application: z
    .object({
      name: z.string(),
      website: z.string().url().nullable().catch(null),
    })
    .nullable()
    .catch(null),
  bookmarked: z.coerce.boolean(),
  boost_id: z.string().nullable().catch(null),
  boosted: z.coerce.boolean().catch(false),
  card: cardSchema.nullable().catch(null),
  embedded_announcement: embeddedAnnouncementSchema.nullable().catch(null),
  content: contentSchema,
  created_at: dateSchema,
  downvotes_count: z.number().catch(0),
  edited_at: dateSchema.nullable().catch(null),
  emojis: filteredArray(customEmojiSchema),
  favourited: z.coerce.boolean(),
  favourites_count: z.number().catch(0),
  group: groupSchema.nullable().catch(null),
  in_reply_to: z.literal(null).catch(null),
  in_reply_to_account_id: z.string().nullable().catch(null),
  in_reply_to_id: z.string().nullable().catch(null),
  interested: z.boolean().catch(true),
  id: z.string(),
  language: z.string().nullable().catch(null),
  media_attachments: filteredArray(attachmentSchema),
  mentions: filteredArray(mentionSchema),
  metrics: metricsSchema.nullable().catch(null),
  muted: z.coerce.boolean(),
  pinned: z.coerce.boolean(),
  pleroma: statusPleromaSchema.optional().catch(undefined),
  poll: pollSchema.nullable().catch(null),
  quote: z.literal(null).catch(null),
  reaction: z.enum(["upvote", "downvote"]).nullable().catch(null),
  reblog: z.literal(null).catch(null),
  reblogged: z.coerce.boolean(),
  reblogs_count: z.number().catch(0),
  replies_count: z.number().catch(0),
  sensitive: z.coerce.boolean(),
  signature: z.string().nullable().catch(null),
  spoiler_text: contentSchema,
  sponsored: z.boolean().catch(false),
  tags: filteredArray(statusTagSchema),
  text: z.string().nullable().catch(""),
  title: z.string().nullable().optional().catch(undefined),
  tombstone: z
    .object({
      reason: z.enum(["deleted", "blocking", "blocked_by", "muted"]),
    })
    .nullable()
    .optional()
    .catch(undefined),
  tv: z
    .object({
      channel_id: z.number(),
      end_time: dateSchema,
      name: z.string(),
      pltv_timespan: z.number(),
      start_time: dateSchema,
    })
    .or(z.undefined())
    .catch(undefined),
  upvotes_count: z.number().catch(0),
  url: z.string().url().catch(""),
  version: z.string().optional().catch("1"),
  visibility: z.enum(["public", "self", "group"]).catch("public"),
  votable: z.boolean().catch(false),
  prediction: predictionSchema.nullish().catch(null),
  news_article: sportNewsArticleSchema.nullish().catch(null),
  sports_game: sportGameDetailsSchema.nullish().catch(null),
  next_status: z.string().url().nullable().catch(null),
});

type BaseStatus = z.infer<typeof baseStatusSchema>;
type TransformableStatus = Omit<
  BaseStatus,
  "reblog" | "quote" | "in_reply_to" | "pleroma"
> & {
  pleroma?: Omit<z.infer<typeof statusPleromaSchema>, "quote">;
};

/** Creates search index from the status. */
const buildSearchIndex = (status: TransformableStatus): string => {
  const pollOptionTitles = status.poll
    ? status.poll.options.map(({ title }) => title)
    : [];
  const mentionedUsernames = getStatusMentions(status as Status).map(
    ({ acct }) => `@${acct}`,
  );

  const fields = [status.content, ...pollOptionTitles, ...mentionedUsernames];

  const searchContent = unescapeHTML(fields.join("\n\n")) || "";
  return (
    new DOMParser().parseFromString(searchContent, "text/html").documentElement
      .textContent || ""
  );
};

/** Add internal fields to the status. */
const transformStatus = <T extends TransformableStatus>({
  pleroma,
  ...status
}: T) => {
  return {
    ...status,
    // sevro signals a boost by presence of boost_id, not a separate `boosted`
    // flag; derive it so clients keying off `boosted` still render the badge.
    boosted: status.boost_id != null,
    media_attachments: status.media_attachments.map((attachment) => ({
      ...attachment,
      sensitive: status.sensitive,
    })),
    contentHtml: stripCompatibilityFeatures(status.content),
    filtered: [],
    hidden: false,
    pleroma: pleroma || undefined,
    search_index: buildSearchIndex(status),
    translatedContent: "",
    translatedTitle: "",
    translatedFrom: "",
  };
};

const embeddedStatusSchema = baseStatusSchema
  .extend({
    quote: baseStatusSchema.transform(transformStatus).nullable().catch(null),
    reblog: baseStatusSchema.transform(transformStatus).nullable().catch(null),
    in_reply_to: baseStatusSchema
      .transform(transformStatus)
      .nullable()
      .catch(null),
  })
  .transform(transformStatus)
  .nullable()
  .catch(null);

const statusSchema = baseStatusSchema
  .extend({
    quote: embeddedStatusSchema,
    reblog: embeddedStatusSchema,
    in_reply_to: embeddedStatusSchema,
    pleroma: statusPleromaSchema
      .extend({
        quote: embeddedStatusSchema,
      })
      .optional()
      .catch(undefined),
  })
  .transform(({ pleroma, ...status }) => {
    return {
      ...status,
      quote: pleroma?.quote || status.quote || null,
      // There's apparently no better way to do this...
      // Just trying to remove the `event` and `quote` keys from the object.
      pleroma: pleroma
        ? (() => {
            const { quote, ...rest } = pleroma;
            return rest;
          })()
        : undefined,
    };
  })
  .transform(transformStatus);

type Status = Resolve<z.infer<typeof statusSchema>>;

export { statusSchema, type Status };
